import { PropsWithChildren, useMemo } from 'react';

import { useDefaultDisclaimerConfiguration } from '@/hooks/useDefaultDisclaimers';
import { useReportError } from '@/hooks/useReportError';

import {
  TenantPresentationConfiguration_EffectiveDisclaimerConfigurationFragment,
  useEffectivePresentationDisclaimerConfigurationQuery,
} from '../graphql/TenantPresentationConfiguration.generated';
import { TenantPresentationConfigurationContext } from './tenantPresentationConfiguration.context';

export interface DisclaimerConfiguration {
  includeLuminaryDisclaimer: boolean;
  presentationCoverDisclaimerTitle: string;
  presentationCoverDisclaimerBody: string;
  presentationFooterDisclaimerTitle: string;
  presentationFooterDisclaimerBody: string;
  presentationStandaloneDisclaimerTitle: string;
  presentationStandaloneDisclaimerBody: string;
}

export function getLegalDisclaimers(
  defaultDisclaimerConfiguration: Omit<
    DisclaimerConfiguration,
    'includeLuminaryDisclaimer'
  >,
  effectiveDisclaimerConfiguration: TenantPresentationConfiguration_EffectiveDisclaimerConfigurationFragment
): DisclaimerConfiguration {
  return {
    includeLuminaryDisclaimer:
      effectiveDisclaimerConfiguration.includeLuminaryDisclaimer,
    presentationCoverDisclaimerTitle:
      effectiveDisclaimerConfiguration.presentationCoverDisclaimerTitle ??
      defaultDisclaimerConfiguration.presentationCoverDisclaimerTitle,
    presentationCoverDisclaimerBody:
      effectiveDisclaimerConfiguration.presentationCoverDisclaimerBody ??
      defaultDisclaimerConfiguration.presentationCoverDisclaimerBody,
    presentationFooterDisclaimerTitle:
      effectiveDisclaimerConfiguration.presentationFooterDisclaimerTitle ??
      defaultDisclaimerConfiguration.presentationFooterDisclaimerTitle,
    presentationFooterDisclaimerBody:
      effectiveDisclaimerConfiguration.presentationFooterDisclaimerBody ??
      defaultDisclaimerConfiguration.presentationFooterDisclaimerBody,
    presentationStandaloneDisclaimerTitle:
      effectiveDisclaimerConfiguration.presentationStandaloneDisclaimerTitle ??
      defaultDisclaimerConfiguration.presentationStandaloneDisclaimerTitle,
    presentationStandaloneDisclaimerBody:
      effectiveDisclaimerConfiguration.presentationStandaloneDisclaimerBody ??
      defaultDisclaimerConfiguration.presentationStandaloneDisclaimerBody,
  };
}

export interface TenantPresentationConfigurationProviderProps {
  /** Undefined while loading; null when the resolved household has no sub-brand. */
  subBrandId: string | null | undefined;
}
export const TenantPresentationConfigurationProvider = ({
  subBrandId,
  children,
}: PropsWithChildren<TenantPresentationConfigurationProviderProps>) => {
  const defaultDisclaimerConfiguration = useDefaultDisclaimerConfiguration();
  const { data, loading, error } =
    useEffectivePresentationDisclaimerConfigurationQuery({
      variables: {
        subBrandID: subBrandId,
      },
      fetchPolicy: 'cache-and-network',
      skip: subBrandId === undefined,
    });
  useReportError('Failed to load effective presentation disclaimers', error);
  const effectiveDisclaimerConfiguration =
    data?.effectiveDisclaimerConfiguration;
  const legalDisclaimers = useMemo<
    TenantPresentationConfigurationContext['legalDisclaimers']
  >(() => {
    if (!effectiveDisclaimerConfiguration) {
      return null;
    }
    return getLegalDisclaimers(
      defaultDisclaimerConfiguration,
      effectiveDisclaimerConfiguration
    );
  }, [defaultDisclaimerConfiguration, effectiveDisclaimerConfiguration]);

  // Do not render with tenant-only or previously cached values while Apollo
  // resolves the effective configuration for a new sub-brand.
  if (subBrandId === undefined || loading || error || !legalDisclaimers) {
    return null;
  }

  return (
    <TenantPresentationConfigurationContext.Provider
      value={{
        legalDisclaimers,
      }}
    >
      {children}
    </TenantPresentationConfigurationContext.Provider>
  );
};
